import type { NavItem } from '@/config/navigation'
import { useMemo } from 'react'
import { useOrganizationStore } from '@/store/organization.store'

import type {
  AccountType,
  FeatureFlagKeyType,
  ModuleTypeEnum,
  PermissionActionEnum,
} from '@scrambl/shared'

import { useAccountType } from './useAccountType'
import { useFeatureFlags } from './useFeatureFlags'
import { useNavigationItems } from './useNavigationItems'
import { usePermissions } from './usePermissions'
import { useSubscriptionFeatures } from './useSubscriptionFeatures'

export interface NavVisibilityContext {
  accountType: AccountType
  checkFeatureFlag: (flag: FeatureFlagKeyType) => boolean
  checkFeature: (featureKey: string) => boolean
  checkPermission: (
    module: ModuleTypeEnum,
    action: PermissionActionEnum,
  ) => boolean
}

/**
 * Mirrors AppSidebar's filteredNavItems: accountTypes first, then an
 * explicitly enabled feature flag wins over the subscription gate, then the
 * item's permission. Shared by OmniSearch nav targets and the settings hub so
 * both surfaces agree with the sidebar on what exists.
 */
export function isNavItemVisible(
  item: NavItem,
  ctx: NavVisibilityContext,
): boolean {
  if (item.accountTypes && !item.accountTypes.includes(ctx.accountType)) {
    return false
  }
  if (item.featureFlag) {
    if (!ctx.checkFeatureFlag(item.featureFlag)) return false
  } else if (
    item.subscriptionFeature &&
    !ctx.checkFeature(item.subscriptionFeature)
  ) {
    return false
  }
  if (
    item.permission &&
    !ctx.checkPermission(item.permission.module, item.permission.action)
  ) {
    return false
  }
  return true
}

/**
 * Navigation items with module-level visibility gating applied to the main
 * nav (hidden items included — search and the settings hub are exactly where
 * non-sidebar destinations stay discoverable), plus the account/role context
 * consumers need for admin and personal gating.
 */
export function useVisibleNavItems() {
  const { mainNavItems, adminNavItems, userMenuItems } = useNavigationItems()
  const { accountType, isPersonal } = useAccountType()
  const { checkPermission, isLoadingPermissions } = usePermissions()
  const { checkFeature } = useSubscriptionFeatures()
  const { currentOrganization } = useOrganizationStore()

  const featureKeys = useMemo(
    () =>
      mainNavItems
        .map(item => item.featureFlag)
        .filter(Boolean) as FeatureFlagKeyType[],
    [mainNavItems],
  )
  const { checkFeatureFlag } = useFeatureFlags(featureKeys)

  const membershipRole = currentOrganization?.userMembership?.role
  const isAdminOrOwner =
    membershipRole === 'OWNER' || membershipRole === 'ADMIN'

  const visibleMainNavItems = useMemo(
    () =>
      mainNavItems.filter(item =>
        isNavItemVisible(item, {
          accountType,
          checkFeatureFlag,
          checkFeature,
          checkPermission,
        }),
      ),
    [
      accountType,
      checkFeature,
      checkFeatureFlag,
      checkPermission,
      mainNavItems,
    ],
  )

  return {
    visibleMainNavItems,
    adminNavItems,
    userMenuItems,
    isAdminOrOwner,
    isPersonal,
    accountType,
    checkPermission,
    isLoadingPermissions,
    checkFeatureFlag,
  }
}
